import { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import api from '../../../services/api';
import { dateFilterColumn } from '../../../utils/dateFilterColumn';
import { selectFilterOptions } from '../../../utils/selectFilterColumn';

const usePolicyDocumentApi = (queryParams) => {
  const { t, i18n } = useTranslation();

  const [fetching, setFetching] = useState(true);
  const [data, setData] = useState([]);
  const [pagination, setPagination] = useState({ totalRowCount: 0, pageSize: 10, pageIndex: 0 });
  const [refreshTrigger, setRefreshTrigger] = useState(0);
  const lastValidQueryParams = useRef(null);

  useEffect(() => {
    const paramsToUse = queryParams && Object.keys(queryParams).length > 0
      ? queryParams
      : lastValidQueryParams.current || { pageNumber: 1, pageSize: 10 };
    lastValidQueryParams.current = paramsToUse;
    setFetching(true);
    api.get(`/v1/policy-documents?${new URLSearchParams(paramsToUse)}`)
      .then((res) => {
        const result = res.data?.data;
        const items = Array.isArray(result) ? result : (result?.data || []);
        setData(items);
        setPagination({
          totalRowCount: result?.totalRowCount || items.length,
          pageIndex: result?.pageIndex ? result.pageIndex - 1 : 0,
          pageSize: result?.pageSize || items.length || 10,
        });
      })
      .finally(() => setFetching(false));
  }, [queryParams, refreshTrigger]);

  // "Kategori" süzgecinin seçenekleri — belge eklenip silindikçe tazelenir.
  const [categories, setCategories] = useState([]);
  useEffect(() => {
    api.get('/v1/policy-documents/categories').then((res) => {
      const list = res.data?.data;
      setCategories(Array.isArray(list) ? list : []);
    });
  }, [refreshTrigger]);

  const refresh = useCallback(() => {
    setRefreshTrigger((p) => p + 1);
  }, []);

  const _delete = useCallback(async (ids) => {
    try {
      if (ids.length === 1) {
        await api.delete(`/v1/policy-documents/${ids[0]}`);
      } else {
        for (const id of ids) {
          await api.delete(`/v1/policy-documents/${id}`);
        }
      }
      setRefreshTrigger((p) => p + 1);
      return true;
    } catch {
      return false;
    }
  }, []);

  const categoryOptions = useMemo(() => categories.map((c) => ({ value: c, label: c })), [categories]);

  // Belgenin durumu yok (PolicyDocumentResponse) — eski "Durum" kolonu hep boş rozet gösteriyordu.
  const columns = useMemo(() => [
    { name: 'id', label: 'ID', options: { filter: false, sort: false, display: 'excluded' } },
    { name: 'title', label: t('policy.documentTitle'), options: { filter: true, sort: true, filterType: 'textField', filterOptions: { defaultOp: 'like' } } },
    {
      name: 'category',
      label: t('policy.category'),
      options: {
        filter: true, sort: true,
        ...selectFilterOptions({ label: t('policy.category'), options: categoryOptions, filterKey: 'category[eq]' }),
        customBodyRender: (v) => v || '-',
      },
    },
    { name: 'version', label: t('policy.version'), options: { filter: false, sort: true } },
    dateFilterColumn('createDate', t('policy.createDate'), i18n.language, { includeTime: true }),
  ], [t, categoryOptions, i18n.language]);

  const objectSchema = useMemo(() => [
    { name: 'title', label: t('policy.documentTitle'), type: 'text', grid: 12, required: true },
    { name: 'description', label: t('policy.description'), type: 'textarea', grid: 12 },
    { name: 'category', label: t('policy.category'), type: 'text', grid: 12, required: true },
  ], [t]);

  return { objectSchema, columns, data, fetching, pagination, _delete, refresh };
};

export default usePolicyDocumentApi;
